/**
 * NetBoxHistoryPanel — История импортов NetBox
 * Sprint 3: rollback кнопка, фильтры, pagination, details panel
 */
import { useEffect, useState, useCallback } from 'react'
import {
  RotateCcw, ChevronDown, ChevronUp, RefreshCw, AlertTriangle,
  CheckCircle2, XCircle, Clock, Filter, ChevronLeft, ChevronRight,
  Layers, Cpu, Cable, Plug, Trash2
} from 'lucide-react'
import { api } from '../../utils/api'
import { toast } from 'react-hot-toast'
import { useConfirmStore } from '../../store/confirmStore'

interface ImportJob {
  id: number
  dc_code: string
  status: string
  progress_pct: number
  progress_msg: string | null
  dry_run: boolean
  conflict_strategy: string
  racks_created: number
  racks_updated: number
  devices_created: number
  devices_updated: number
  ports_created: number
  cables_created: number
  cables_skipped: number
  tenants_created?: number
  tenants_updated?: number
  reservations_created?: number
  reservations_updated?: number
  errors: string | null
  created_by: string | null
  client_ip: string | null
  created_at: string | null
  completed_at: string | null
  rolled_back_at: string | null
  rollback_deleted_racks: number | null
  rollback_deleted_devices: number | null
  rollback_deleted_ports: number | null
  rollback_deleted_cables: number | null
  rollback_deleted_reservations?: number | null
  rollback_deleted_tenants?: number | null
}

interface Props {
  dcCode: string
}

const STATUS_FILTERS = [
  { value: '', label: 'Все' },
  { value: 'completed', label: 'Успешно' },
  { value: 'completed_with_errors', label: 'С ошибками' },
  { value: 'failed', label: 'Ошибка' },
]

function StatusBadge({ status, rolledBack }: { status: string; rolledBack: boolean }) {
  if (rolledBack) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
        <RotateCcw size={10} /> Откатан
      </span>
    )
  }
  switch (status) {
    case 'completed':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400"><CheckCircle2 size={10}/> Успешно</span>
    case 'completed_with_errors':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400"><AlertTriangle size={10}/> С ошибками</span>
    case 'failed':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400"><XCircle size={10}/> Ошибка</span>
    case 'running':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400"><RefreshCw size={10} className="animate-spin"/> Выполняется</span>
    case 'pending':
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400"><Clock size={10}/> Ожидание</span>
    default:
      return <span className="px-2 py-0.5 rounded text-xs bg-gray-700 text-gray-400">{status}</span>
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}

export function NetBoxHistoryPanel({ dcCode }: Props) {
  const [jobs, setJobs] = useState<ImportJob[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(0)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [rolling, setRolling] = useState<number | null>(null)
  const limit = 8

  const load = useCallback(async () => {
    if (!dcCode) return
    setLoading(true)
    try {
      const params = new URLSearchParams({
        dc_code: dcCode,
        limit: String(limit),
        offset: String(page * limit),
      })
      if (statusFilter) params.set('status', statusFilter)
      const res = await api.get(`/api/integrations/netbox/history?${params}`)
      setJobs(res.data.history || [])
      setTotal(res.data.total || 0)
    } catch {
      toast.error('Ошибка загрузки истории')
    } finally {
      setLoading(false)
    }
  }, [dcCode, statusFilter, page])

  useEffect(() => { load() }, [load])

  const handleRollback = async (job: ImportJob) => {
    // First dry-run to preview
    try {
      const preview = await api.post(`/api/integrations/netbox/jobs/${job.id}/rollback?dry_run=true`)
      const { would_delete: wd } = preview.data
      const parts = []
      if (wd.racks) parts.push(`${wd.racks} стоек`)
      if (wd.devices) parts.push(`${wd.devices} устройств`)
      if (wd.reservations) parts.push(`${wd.reservations} броней`)
      if (wd.tenants) parts.push(`${wd.tenants} арендаторов`)
      if (wd.ports) parts.push(`${wd.ports} портов`)
      if (wd.cables) parts.push(`${wd.cables} кабелей`)
      const msg = `Будет удалено: ${parts.length ? parts.join(', ') : '0 объектов'}. Продолжить?`
      const confirmed = await useConfirmStore.getState().confirm({
        title: 'Откат импорта NetBox',
        message: msg,
        confirmText: 'Откатить',
        cancelText: 'Отмена'
      });
      if (!confirmed) return

      setRolling(job.id)
      await api.post(`/api/integrations/netbox/jobs/${job.id}/rollback`)
      toast.success(`Импорт #${job.id} откатан`)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Ошибка отката')
    } finally {
      setRolling(null)
    }
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <div className="space-y-3">
      {/* Filters + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-text-secondary" />
        <div className="flex gap-1">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => { setStatusFilter(f.value); setPage(0) }}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                statusFilter === f.value
                  ? 'bg-accent-primary text-white'
                  : 'bg-bg-panel text-text-secondary hover:text-text-primary border border-border'
              }`}
            >{f.label}</button>
          ))}
        </div>
        <button
          onClick={load}
          className="ml-auto p-1.5 text-text-secondary hover:text-text-primary rounded"
          title="Обновить"
        ><RefreshCw size={14} className={loading ? 'animate-spin' : ''} /></button>
      </div>

      {/* Jobs list */}
      {loading && jobs.length === 0 ? (
        <div className="text-center text-text-secondary py-8">Загрузка...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center text-text-secondary py-8">
          <Clock size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">История импортов пуста</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {jobs.map(job => (
            <div key={job.id} className="bg-bg-panel rounded-lg border border-border overflow-hidden">
              {/* Header row */}
              <div
                className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white/5 transition-all"
                onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}
              >
                <span className="text-xs font-mono text-text-secondary w-8">#{job.id}</span>
                <StatusBadge status={job.status} rolledBack={!!job.rolled_back_at} />
                {job.dry_run && (
                  <span className="px-1.5 py-0.5 rounded text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20">Dry Run</span>
                )}
                <span className="text-xs text-text-secondary">{formatDate(job.created_at)}</span>
                <div className="ml-auto flex items-center gap-2">
                  {/* Quick stats */}
                  <div className="hidden sm:flex items-center gap-2 text-xs text-text-secondary">
                    {job.racks_created > 0 && <span className="flex items-center gap-1"><Layers size={10}/> {job.racks_created}</span>}
                    {job.devices_created > 0 && <span className="flex items-center gap-1"><Cpu size={10}/> {job.devices_created}</span>}
                    {job.cables_created > 0 && <span className="flex items-center gap-1"><Cable size={10}/> {job.cables_created}</span>}
                  </div>
                  {expandedId === job.id ? <ChevronUp size={14} className="text-text-secondary"/> : <ChevronDown size={14} className="text-text-secondary"/>}
                </div>
              </div>

              {/* Expanded detail */}
              {expandedId === job.id && (
                <div className="border-t border-border px-3 py-3 space-y-3">
                  {/* Stats grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { label: 'Стойки', icon: <Layers size={12}/>, c: job.racks_created, u: job.racks_updated },
                      { label: 'Устройства', icon: <Cpu size={12}/>, c: job.devices_created, u: job.devices_updated },
                      { label: 'Порты', icon: <Plug size={12}/>, c: job.ports_created },
                      { label: 'Кабели', icon: <Cable size={12}/>, c: job.cables_created, s: job.cables_skipped },
                    ].map(s => (
                      <div key={s.label} className="bg-bg-base rounded p-2">
                        <div className="flex items-center gap-1 text-text-secondary text-xs mb-1">{s.icon} {s.label}</div>
                        <div className="text-text-primary font-medium text-sm">+{s.c ?? 0}</div>
                        {(s.u !== undefined && s.u > 0) && <div className="text-yellow-400 text-xs">~{s.u} обновл.</div>}
                        {(s.s !== undefined && s.s > 0) && <div className="text-gray-400 text-xs">{s.s} пропущ.</div>}
                      </div>
                    ))}
                  </div>

                  {/* Meta info */}
                  <div className="text-xs text-text-secondary space-y-0.5">
                    {job.client_ip && <div>IP: <span className="text-text-primary">{job.client_ip}</span></div>}
                    {job.created_by && <div>Пользователь: <span className="text-text-primary font-mono">{job.created_by.replace(/username='(.*?)' .*/, '$1')}</span></div>}
                    {job.conflict_strategy && <div>Стратегия: <span className="text-text-primary">{job.conflict_strategy}</span></div>}
                    {job.completed_at && <div>Завершён: <span className="text-text-primary">{formatDate(job.completed_at)}</span></div>}
                  </div>

                  {/* Rollback info */}
                  {job.rolled_back_at && (
                    <div className="rounded bg-purple-500/10 border border-purple-500/20 p-2 text-xs text-purple-300">
                      <RotateCcw size={12} className="inline mr-1" />
                      Откатан {formatDate(job.rolled_back_at)}: удалено {job.rollback_deleted_racks ?? 0} стоек,
                      {' '}{job.rollback_deleted_devices ?? 0} устройств,
                      {job.rollback_deleted_reservations ? ` ${job.rollback_deleted_reservations} броней,` : ''}
                      {job.rollback_deleted_tenants ? ` ${job.rollback_deleted_tenants} арендаторов,` : ''}
                      {' '}{job.rollback_deleted_ports ?? 0} портов,
                      {' '}{job.rollback_deleted_cables ?? 0} кабелей
                    </div>
                  )}

                  {/* Errors */}
                  {job.errors && (
                    <div className="rounded bg-red-500/10 border border-red-500/20 p-2 text-xs text-red-300 font-mono whitespace-pre-wrap max-h-24 overflow-auto">
                      {job.errors}
                    </div>
                  )}

                  {/* Actions */}
                  {!job.dry_run && !job.rolled_back_at && job.status !== 'failed' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRollback(job)}
                        disabled={rolling === job.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/60 text-xs font-medium transition-all disabled:opacity-50"
                      >
                        {rolling === job.id ? <RefreshCw size={12} className="animate-spin"/> : <Trash2 size={12}/>}
                        Откатить импорт
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-secondary">
          <span>Показано {page * limit + 1}–{Math.min((page + 1) * limit, total)} из {total}</span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="p-1.5 rounded border border-border disabled:opacity-30 hover:text-text-primary transition-all"
            ><ChevronLeft size={14}/></button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages - 1}
              className="p-1.5 rounded border border-border disabled:opacity-30 hover:text-text-primary transition-all"
            ><ChevronRight size={14}/></button>
          </div>
        </div>
      )}
    </div>
  )
}
